import { useCallback, useState } from 'react';

interface GridMetrics {
  /** Cards per row from the measured container width. 0 = not measurable yet. */
  columns: number;
  /** Document-space offset of the grid, for a window virtualizer's `scrollMargin`. */
  scrollMargin: number;
}

/**
 * Measures a card grid's container to drive windowed virtualization.
 *
 * Returns a **callback ref** (not a stable object ref) so measurement re-attaches
 * whenever the measured node mounts or remounts — e.g. when the grid is unmounted
 * while an error state is shown and remounted on retry. React 19 invokes the
 * returned cleanup when the node detaches.
 *
 * `columns` is `floor(width / cardWidth)` (min 1 for any positive width), or `0`
 * until a real width is measured (SSR / first paint / no-layout test env) so callers
 * can fall back to non-virtualized rendering. `scrollMargin` is re-measured on the
 * node's own resize, on window resize, and on `document.body` resize — the last of
 * which catches layout shifts *above* the grid (e.g. filters expanding) that move
 * its document offset without resizing the node itself.
 */
export const useGridMetrics = (cardWidth: number) => {
  const [metrics, setMetrics] = useState<GridMetrics>({ columns: 0, scrollMargin: 0 });

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const measure = () => {
        const width = node.clientWidth;
        const columns = width > 0 ? Math.max(1, Math.floor(width / cardWidth)) : 0;
        const scrollMargin = node.getBoundingClientRect().top + window.scrollY;
        setMetrics((prev) =>
          prev.columns === columns && prev.scrollMargin === scrollMargin
            ? prev
            : { columns, scrollMargin },
        );
      };

      measure();

      const cleanups: Array<() => void> = [];
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        if (document.body) observer.observe(document.body);
        cleanups.push(() => observer.disconnect());
      }
      window.addEventListener('resize', measure);
      cleanups.push(() => window.removeEventListener('resize', measure));

      return () => cleanups.forEach((fn) => fn());
    },
    [cardWidth],
  );

  return { ref, columns: metrics.columns, scrollMargin: metrics.scrollMargin };
};
